<script lang="ts">
	import {
		Globe,
		Shield,
		Zap,
		TrendingUp,
		CheckCircle,
		ArrowRight,
		Users,
		Clock,
		FileCheck,
		Building2,
		Plane,
		Laptop
	} from '@lucide/svelte';

	type Target = 'glopay' | 'svbl' | 'iglu';
	type VariantKey = 1 | 2 | 3 | 4 | 5;

	interface Props {
		placement?: string;
		audience?: 'talent' | 'company';
		forceVariant?: number;
		forceVisualStyle?: number;
		forceTarget?: Target;
		orientation?: 'horizontal' | 'vertical';
	}

	let {
		placement = 'default',
		audience = 'company',
		forceVariant,
		forceVisualStyle,
		forceTarget,
		orientation = 'horizontal'
	}: Props = $props();

	const visible = $state(Math.random() < 0.1);

	const targets: Target[] = ['glopay', 'svbl', 'iglu'];

	const baseUrls: Record<Target, string> = {
		glopay: 'https://glopay.com',
		svbl: 'https://svbl.co.th',
		iglu: 'https://iglu.net'
	};

	const variantsByTarget = {
		glopay: {
			talent: {
				1: {
					icon: Globe,
					title: 'Got the job? Now get paid globally.',
					subtitle:
						'Invoice international clients in 100+ currencies. EU-compliant company setup in 5 minutes.',
					cta: 'Start invoicing free',
					badge: '5% per invoice'
				},
				2: {
					icon: Shield,
					title: 'Invoice like a company, stay a freelancer.',
					subtitle:
						'EU partnership gives your clients confidence. You keep control and 95% of earnings.',
					cta: 'See how it works',
					badge: 'No monthly fees'
				},
				3: {
					icon: Zap,
					title: 'Hired by a US client? Invoice them properly.',
					subtitle:
						'They need a real company on the invoice. GloPay forms an EU partnership for you automatically.',
					cta: 'Get started free',
					badge: 'Ready in 24h'
				},
				4: {
					icon: TrendingUp,
					title: 'Turn that remote job into professional invoices.',
					subtitle:
						"Clients pay faster when invoices come from a registered EU company. That's you + Glopay.",
					cta: 'Create first invoice',
					badge: '100% free signup'
				},
				5: {
					icon: CheckCircle,
					title: 'Compliance solved. Invoice ready. Get paid.',
					subtitle:
						'Estonian company partnership handles taxes, compliance, reporting. You just send invoices.',
					cta: 'Try it free',
					badge: '5 min setup'
				}
			},
			company: {
				1: {
					icon: Globe,
					title: 'Hiring global talent? Pay them effortlessly.',
					subtitle:
						'Pay contractors in 100+ countries with one platform. They get professional invoicing, you get simple payments.',
					cta: 'Start hiring globally',
					badge: '5% per payment'
				},
				2: {
					icon: Users,
					title: 'Your contractors need proper invoicing.',
					subtitle:
						'Glopay gives them EU company partnerships. You get compliant invoices, they get professional setup.',
					cta: 'See how it works',
					badge: 'No setup fees'
				},
				3: {
					icon: Clock,
					title: 'Paying international contractors is messy.',
					subtitle:
						'Multiple currencies, tax forms, compliance headaches. Glopay handles it all in one platform.',
					cta: 'Simplify payments',
					badge: 'Ready in 24h'
				},
				4: {
					icon: FileCheck,
					title: 'Compliant invoices from all your contractors.',
					subtitle:
						'Stop chasing invoices. Glopay ensures every contractor sends proper, tax-compliant documentation.',
					cta: 'Get started',
					badge: 'Free for companies'
				},
				5: {
					icon: Shield,
					title: 'Scale your remote team without payment chaos.',
					subtitle:
						'One dashboard for all contractor payments. Multi-currency, auto-compliance, instant tracking.',
					cta: 'Start paying easily',
					badge: 'Trusted by 500+ companies'
				}
			}
		},
		svbl: {
			talent: {
				1: {
					icon: Plane,
					title: 'Moving to Thailand? Get your visa sorted.',
					subtitle:
						'SVBL handles work permits, extensions, and relocation. Free initial consultation with immigration experts.',
					cta: 'Get free consultation',
					badge: 'Free consultation'
				},
				2: {
					icon: Shield,
					title: 'Work legally in Thailand. Zero stress.',
					subtitle:
						'Expert visa and work permit processing. 10+ years navigating Thai immigration for expats and professionals.',
					cta: 'Talk to an expert',
					badge: '10+ years experience'
				},
				3: {
					icon: Building2,
					title: 'Starting a Thai business? Own it 100%.',
					subtitle:
						'BOI company setup gives foreigners full ownership and up to 13 years tax exemption. SVBL handles the application.',
					cta: 'Check eligibility',
					badge: 'Full foreign ownership'
				},
				4: {
					icon: FileCheck,
					title: "Visa expiring? Don't overstay.",
					subtitle:
						'SVBL finds your best option — extension, category switch, or long-term visa — and handles the paperwork.',
					cta: 'Check your options',
					badge: 'Prevent overstay'
				},
				5: {
					icon: Globe,
					title: 'Relocating your whole life to Thailand?',
					subtitle:
						'Visa, housing, banking, schools — SVBL manages every detail of your move so nothing gets missed.',
					cta: 'Plan your move',
					badge: 'One-stop relocation'
				}
			},
			company: {
				1: {
					icon: Building2,
					title: 'Expanding to Thailand? Set up right.',
					subtitle:
						'Company registration, BOI promotion, foreign ownership — full legal compliance from day one.',
					cta: 'Set up your entity',
					badge: 'Free eligibility review'
				},
				2: {
					icon: Users,
					title: 'Moving staff to Thailand? We handle permits.',
					subtitle:
						'Work permits and visas for your foreign employees. Direct government liaison, bulk processing.',
					cta: 'Get team permits',
					badge: 'Efficient processing'
				},
				3: {
					icon: Globe,
					title: 'Want 100% ownership of your Thai entity?',
					subtitle:
						'BOI promotion waives the 51% local ownership rule. Up to 13 years corporate tax exemption.',
					cta: 'Check BOI eligibility',
					badge: 'Tax-free up to 13 years'
				},
				4: {
					icon: Shield,
					title: 'Thai compliance is complex. We simplify it.',
					subtitle:
						'Corporate filings, tax obligations, labor law, immigration — SVBL keeps your business compliant.',
					cta: 'Talk to legal experts',
					badge: '10+ years expertise'
				},
				5: {
					icon: FileCheck,
					title: 'Building a team in Bangkok?',
					subtitle:
						'Office setup, contracts, accounting, legal advisory — everything your Thai operation needs to run.',
					cta: 'Get started',
					badge: 'End-to-end support'
				}
			}
		},
		iglu: {
			talent: {
				1: {
					icon: Globe,
					title: 'Work from Thailand with real employment.',
					subtitle:
						'Iglu provides legal work setup, projects, and a community of 200+ digital professionals.',
					cta: 'Join Iglu',
					badge: '200+ professionals'
				},
				2: {
					icon: Zap,
					title: 'Freelancing? Get stability without losing freedom.',
					subtitle: 'Choose your projects, earn performance-based pay, let Iglu handle the admin.',
					cta: 'See open roles',
					badge: 'Revenue sharing'
				},
				3: {
					icon: Laptop,
					title: 'Relocate to Bangkok and code from paradise.',
					subtitle:
						'Full employment, work permit, and relocation support. Thailand, Vietnam, or Hong Kong.',
					cta: 'Explore locations',
					badge: 'Relocation included'
				},
				4: {
					icon: TrendingUp,
					title: "Top devs earn more with Iglu's revenue model.",
					subtitle:
						'Performance-based pay that rewards your skills. International clients, your project choices.',
					cta: 'Check compensation',
					badge: 'Above market rate'
				},
				5: {
					icon: Users,
					title: 'Join 200+ remote pros from 37 countries.',
					subtitle:
						"Developer community, knowledge sharing, cross-project collaboration. Remote doesn't mean alone.",
					cta: 'Join the community',
					badge: '37 countries'
				}
			},
			company: {
				1: {
					icon: Users,
					title: 'Need remote devs? 200+ ready now.',
					subtitle:
						'Vetted tech talent at competitive rates. Full-stack teams, managed delivery, 5 countries.',
					cta: 'Hire talent',
					badge: 'Available now'
				},
				2: {
					icon: Zap,
					title: 'Scale your dev team without the overhead.',
					subtitle:
						'Add experienced developers in days. No recruitment costs, no onboarding delays.',
					cta: 'Scale your team',
					badge: 'Rapid team scaling'
				},
				3: {
					icon: Globe,
					title: 'Building distributed? We handle employment.',
					subtitle:
						'Legal compliance, HR, infrastructure across Thailand, Vietnam, Hong Kong, Estonia, UK.',
					cta: 'Build your team',
					badge: '5 countries'
				},
				4: {
					icon: Shield,
					title: 'English-fluent support teams, SEA prices.',
					subtitle:
						'Customer service from Thailand and Vietnam. Trained, managed, and ready for your brand.',
					cta: 'Get support teams',
					badge: 'No language barriers'
				},
				5: {
					icon: TrendingUp,
					title: 'Full-service tech partner. Global talent.',
					subtitle: 'Web, mobile, cloud, QA — Iglu covers the full stack. AWS, Azure, GCP fluent.',
					cta: 'Start a project',
					badge: '10+ years delivery'
				}
			}
		}
	} as const;

	const visualStyles = {
		1: {
			container:
				'bg-white border-l-4 border-blue-500 rounded-lg shadow-none hover:shadow-md transition-shadow',
			iconBg: 'bg-blue-50',
			iconColor: 'text-blue-600',
			titleColor: 'text-gray-900',
			subtitleColor: 'text-gray-600',
			badgeBg: 'bg-blue-50',
			badgeColor: 'text-blue-700',
			ctaBg: 'bg-blue-600 hover:bg-blue-700',
			ctaColor: 'text-white'
		},
		2: {
			container:
				'bg-gray-50 border border-gray-200 rounded-xl hover:border-purple-300 transition-colors',
			iconBg: 'bg-purple-100',
			iconColor: 'text-purple-600',
			titleColor: 'text-gray-900',
			subtitleColor: 'text-gray-700',
			badgeBg: 'bg-purple-100',
			badgeColor: 'text-purple-700',
			ctaBg: 'bg-purple-600 hover:bg-purple-700',
			ctaColor: 'text-white'
		},
		3: {
			container: 'bg-white border border-gray-200 rounded-xl hover:border-cyan-400 transition-all',
			iconBg: 'bg-cyan-50',
			iconColor: 'text-cyan-600',
			titleColor: 'text-gray-900',
			subtitleColor: 'text-gray-600',
			badgeBg: 'bg-cyan-50',
			badgeColor: 'text-cyan-700',
			ctaBg: 'bg-cyan-600 hover:bg-cyan-700',
			ctaColor: 'text-white'
		},
		4: {
			container: 'bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-0 rounded-2xl',
			iconBg: 'bg-white/80 backdrop-blur-sm',
			iconColor: 'text-blue-600',
			titleColor: 'text-gray-900',
			subtitleColor: 'text-gray-700',
			badgeBg: 'bg-white/80 backdrop-blur-sm',
			badgeColor: 'text-blue-700',
			ctaBg: 'bg-blue-600 hover:bg-blue-700',
			ctaColor: 'text-white'
		},
		5: {
			container:
				'bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-lg transition-shadow',
			iconBg: 'bg-gradient-to-br from-blue-50 to-purple-50',
			iconColor: 'text-blue-600',
			titleColor: 'text-gray-900',
			subtitleColor: 'text-gray-600',
			badgeBg: 'bg-gradient-to-r from-blue-50 to-purple-50',
			badgeColor: 'text-gray-700',
			ctaBg: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700',
			ctaColor: 'text-white'
		}
	} as const;

	let randomVariant = $state(Math.floor(Math.random() * 5) + 1);
	let randomVisualStyle = $state(Math.floor(Math.random() * 5) + 1);
	let randomTarget = $state(targets[Math.floor(Math.random() * targets.length)]);

	const target = $derived(forceTarget || randomTarget);
	const variant = $derived((forceVariant || randomVariant) as VariantKey);
	const visualStyle = $derived((forceVisualStyle || randomVisualStyle) as VariantKey);

	const current = $derived(variantsByTarget[target][audience][variant]);
	const Icon = $derived(current.icon);
	const style = $derived(visualStyles[visualStyle]);

	const trackingUrl = $derived(
		`${baseUrls[target]}?utm_source=${placement}&utm_medium=cta-${orientation}&utm_campaign=talentprofile-${audience}&utm_content=variant-${variant}-style-${visualStyle}`
	);
</script>

{#if visible}
	{#if orientation === 'vertical'}
		<div class="{style.container} flex h-full flex-col p-8">
			<div class="mb-4 flex items-center justify-between">
				<div class="flex h-12 w-12 items-center justify-center {style.iconBg} shrink-0 rounded-lg">
					<Icon class="h-6 w-6 {style.iconColor}" />
				</div>
				<span class="{style.badgeBg} {style.badgeColor} rounded-full px-3 py-1 text-xs font-medium">
					{current.badge}
				</span>
			</div>

			<h3 class="mb-2 text-base font-semibold {style.titleColor} leading-tight">{current.title}</h3>
			<p class="{style.subtitleColor} mb-4 flex-1 text-sm leading-relaxed">{current.subtitle}</p>

			<a
				href={trackingUrl}
				target="_blank"
				rel="noopener noreferrer sponsored"
				class="{style.ctaBg} {style.ctaColor} mt-auto flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors"
			>
				{current.cta}
				<ArrowRight class="h-4 w-4" />
			</a>
		</div>
	{:else}
		<div class="{style.container} p-5 md:p-6">
			<div class="flex flex-col items-start gap-4 md:flex-row md:items-center md:gap-6">
				<div
					class="hidden h-12 w-12 items-center justify-center md:flex {style.iconBg} superellipse shrink-0"
				>
					<Icon class="h-6 w-6 {style.iconColor}" />
				</div>

				<div class="min-w-0 flex-1">
					<div class="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
						<h3 class="text-lg font-semibold {style.titleColor} leading-tight">{current.title}</h3>
						<span
							class="hidden sm:inline-block {style.badgeBg} {style.badgeColor} w-fit rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap"
						>
							{current.badge}
						</span>
					</div>
					<p class="{style.subtitleColor} text-sm leading-relaxed">{current.subtitle}</p>
				</div>

				<a
					href={trackingUrl}
					target="_blank"
					rel="noopener noreferrer sponsored"
					class="{style.ctaBg} {style.ctaColor} flex w-full shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors md:w-auto"
				>
					{current.cta}
					<ArrowRight class="h-4 w-4" />
				</a>
			</div>
		</div>
	{/if}
{/if}
